import { useEffect, useRef, useState } from "react";

/**
 * 複製自 src/screen/Onboarding/components/Otp.tsx（OtpBoxes / Resend / OtpContent），
 * 供 addAccount 頁內註冊流程使用；不與 onboarding 共用，避免互相波及。
 */

interface OtpBoxesProps {
  otp: string[];
  setOtp: (v: string[]) => void;
  disabled?: boolean;
  /** 重置訊號：父層每次「驗證失敗清空重填」時 +1，本元件把游標移回第一格。 */
  resetSignal?: number;
}

export const OtpBoxes = ({ otp, setOtp, disabled, resetSignal }: OtpBoxesProps) => {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => {
    if (resetSignal === undefined || resetSignal === 0) return;
    inputs.current[0]?.focus();
  }, [resetSignal]);
  /** 從第 start 格起依序填入多個數字（簡訊自動填入/貼上整組驗證碼會一次給整串） */
  const fillFrom = (start: number, raw: string) => {
    const digits = raw.replace(/\D/g, "").split("");
    if (digits.length === 0) return;
    const n = [...otp];
    digits.slice(0, otp.length - start).forEach((d, k) => {
      n[start + k] = d;
    });
    setOtp(n);
    const lastFilled = Math.min(start + digits.length, otp.length) - 1;
    inputs.current[Math.min(lastFilled + 1, otp.length - 1)]?.focus();
  };
  const setDigit = (i: number, v: string) => {
    if (disabled) return;
    // 自動填入/貼上：整組驗證碼會塞進單一格 → 分配到各格（不可只取第一碼）
    if (v.replace(/\D/g, "").length > 1) {
      fillFrom(i, v);
      return;
    }
    if (!/^\d?$/.test(v)) return;
    const n = [...otp];
    n[i] = v;
    setOtp(n);
    if (v && i < otp.length - 1) inputs.current[i + 1]?.focus();
  };
  const onKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  return (
    <div className="flex gap-2 justify-between">
      {otp.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          value={d}
          inputMode="numeric"
          // 簡訊驗證碼自動填入：只掛第一格（iOS/Android 會把「整組」塞進這一格，
          // 再由 setDigit/fillFrom 分配到各格）。不可設 maxLength=1——會把整組截成單碼。
          autoComplete={i === 0 ? "one-time-code" : "off"}
          name={i === 0 ? "one-time-code" : undefined}
          disabled={disabled}
          onChange={(e) => setDigit(i, e.target.value)}
          onPaste={(e) => {
            e.preventDefault();
            fillFrom(i, e.clipboardData.getData("text"));
          }}
          onKeyDown={(e) => onKey(i, e)}
          autoFocus={i === 0}
          className="w-[46px] h-[54px] text-center text-[22px] font-bold text-[#333] rounded-[12px] outline-none box-border"
          style={{
            border: `1.5px solid ${d ? "#2196f3" : "#E5E7EB"}`,
            background: d ? "#EFF6FF" : "#F8FAFC",
            opacity: disabled ? 0.6 : 1,
          }}
        />
      ))}
    </div>
  );
};

interface ResendProps {
  /** 使用者按下「重新傳送驗證碼」時呼叫，實際打送出 OTP API 由呼叫端負責。 */
  onResend: () => void;
  /** 冷卻到期時間（epoch 秒），由後端決定。 */
  resendUntil?: number;
}

/** 依到期時間算還剩幾秒（向上取整；已到期回 0）。 */
export const secondsUntil = (untilSec: number | undefined, nowMs: number): number => {
  if (!untilSec || !Number.isFinite(untilSec)) return 0;
  const left = Math.ceil(untilSec - nowMs / 1000);
  return left > 0 ? left : 0;
};

export const Resend = ({ onResend, resendUntil }: ResendProps) => {
  const [secs, setSecs] = useState(() => secondsUntil(resendUntil, Date.now()));
  // 每秒依「現在時間」重算，而不是自減：分頁被凍結時 setInterval 被節流，自減會愈算愈慢。
  useEffect(() => {
    const tick = () => setSecs(secondsUntil(resendUntil, Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [resendUntil]);
  return (
    <div className="text-center text-[13px] text-[#94A3B8] mt-4">
      {secs > 0 ? (
        `${secs} 秒後可重新傳送`
      ) : (
        <span className="text-[#2196f3] font-semibold cursor-pointer" onClick={onResend}>
          重新傳送驗證碼
        </span>
      )}
    </div>
  );
};

interface OtpContentProps {
  otp: string[];
  setOtp: (v: string[]) => void;
  hideIcon?: boolean;
  /** 使用者按下「重新傳送驗證碼」時呼叫（實際打 API）。 */
  onResend: () => void;
  /** 驗證進行中時鎖住輸入框，避免重複送出。 */
  disabled?: boolean;
  /** 驗證失敗清空重填時 +1 → 游標自動回到第一格。 */
  resetSignal?: number;
  /** 簡訊確實寄出去了才顯示重送倒數。 */
  sent?: boolean;
  /** 冷卻到期時間（epoch 秒），由後端決定。 */
  resendUntil?: number;
}

export const OtpContent = ({ otp, setOtp, hideIcon, onResend, disabled, resetSignal, sent = true, resendUntil }: OtpContentProps) => (
  <>
    {!hideIcon && (
      <div className="w-16 h-16 rounded-full bg-[#EFF6FF] flex items-center justify-center mx-auto mt-1.5 mb-[22px]">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="#2196f3" strokeWidth="1.8" />
          <path d="M4 8l8 5 8-5" stroke="#2196f3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    )}
    <OtpBoxes otp={otp} setOtp={setOtp} disabled={disabled} resetSignal={resetSignal} />
    {sent && <Resend onResend={onResend} resendUntil={resendUntil} />}
  </>
);
